import { useState } from 'react';
import { useStaffAuth } from '../../state/useStaffAuth';
import type { StaffAuth } from '../../lib/auth';

// ─────────────────────────────────────────────────────────────────────
// Internal staff sign-in (Microsoft Entra ID).
//
// Renders NOTHING when Entra is unconfigured, which is everywhere today. That
// is what keeps this slice invisible to the running demo environment: no
// VITE_ENTRA_* values means no panel, no MSAL, and a login screen byte-for-byte
// as it was.
//
// What it deliberately does NOT do is let you into the portal. Signing in with
// Microsoft proves identity and nothing more; Starpoint decides authority
// server-side, from its own membership tables, and the API is not wired up yet
// (VITE_API_URL is unset). Granting portal access here would be inventing an
// authorization decision in the browser — exactly the thing the whole auth
// design refuses to do. The copy says so, in as many words, so nobody reads a
// successful Microsoft sign-in as a permission.
// ─────────────────────────────────────────────────────────────────────

export interface StaffAuthPanelProps {
  /** Injectable for tests; defaults to the app-wide instance. */
  auth?: StaffAuth;
}

/**
 * What the token probe found, already reduced to display text.
 *
 * The token never enters this state — only a message derived from its LENGTH.
 * Keeping the token out of component state is deliberate: state ends up in
 * React DevTools, in error overlays, and in screenshots.
 */
type ProbeResult = { ok: boolean; text: string } | null;

const TOKEN_OK = (length: number) =>
  `Fikk gyldig API-token (${length} tegn). Token vises aldri og lagres ikke av Starpoint.`;
const TOKEN_FAILED = 'Fikk ikke token. Logg inn på nytt.';

/** The Microsoft mark — four squares — so the button is recognisable at a glance. */
function MicrosoftMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="0" y="0" width="7.5" height="7.5" fill="#F25022" />
      <rect x="8.5" y="0" width="7.5" height="7.5" fill="#7FBA00" />
      <rect x="0" y="8.5" width="7.5" height="7.5" fill="#00A4EF" />
      <rect x="8.5" y="8.5" width="7.5" height="7.5" fill="#FFB900" />
    </svg>
  );
}

export function StaffAuthPanel({ auth }: StaffAuthPanelProps) {
  const { state, available, signIn, signOut, getApiToken } = useStaffAuth(auth);
  const [probe, setProbe] = useState<ProbeResult>(null);
  const [probing, setProbing] = useState(false);

  if (state.status === 'disabled') return null;

  if (state.status === 'misconfigured') {
    // Loud rather than silent: a half-configured sign-in looks deliberate and
    // is the hardest kind of breakage to diagnose from the outside.
    return (
      <div className="mt-4 text-[11px] text-rose px-3 py-2 rounded bg-rose/10 border border-rose/20">
        <p className="font-medium">Microsoft-innlogging er feilkonfigurert</p>
        {state.missing.length > 0 && <p className="mt-1">Mangler: {state.missing.join(', ')}</p>}
        {state.problems.map((p) => (
          <p key={p} className="mt-1">
            {p}
          </p>
        ))}
      </div>
    );
  }

  const runProbe = async () => {
    setProbing(true);
    setProbe(null);
    try {
      const token = await getApiToken();
      // Only the LENGTH is surfaced. Showing the token — or logging it — would
      // put it in screenshots and in the console ring buffer that the
      // bug-report flow mails out (lib/consoleCapture.ts).
      setProbe(
        token ? { ok: true, text: TOKEN_OK(token.length) } : { ok: false, text: TOKEN_FAILED },
      );
    } catch {
      setProbe({ ok: false, text: TOKEN_FAILED });
    } finally {
      setProbing(false);
    }
  };

  const busy = state.status === 'signing-in' || probing;

  return (
    <div
      className="rounded-xl border border-line bg-paper p-5 h-full"
      data-testid="staff-auth-panel"
    >
      <h3 className="font-display text-base font-semibold text-ink">Ansatt i Starpoint?</h3>
      <p className="text-xs text-ink-3 mt-1">
        Logg inn med Microsoft-kontoen din. Dette bekrefter hvem du er, men gir foreløpig ingen
        tilgang i portalen — tilgang styres av Starpoint, ikke av Microsoft.
      </p>

      {state.status === 'signed-in' && (
        <div className="mt-4 text-xs" data-testid="staff-auth-identity">
          <p className="font-medium">{state.identity.displayName ?? 'Innlogget'}</p>
          {state.identity.email && <p className="text-ink-3">{state.identity.email}</p>}
        </div>
      )}

      {state.status === 'error' && (
        <div
          className="mt-4 text-xs text-rose px-3 py-2 rounded bg-rose/10 border border-rose/20"
          data-testid="staff-auth-error"
        >
          {state.message}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {state.status === 'signed-in' ? (
          <>
            <button type="button" className="btn-ghost text-xs" onClick={() => void signOut()}>
              Logg ut av Microsoft
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={busy}
              onClick={() => void runProbe()}
            >
              {probing ? 'Henter token…' : 'Test API-token'}
            </button>
          </>
        ) : (
          // THE MICROSOFT BUTTON LOOKS LIKE ONE: white, bordered, the four
          // squares in front — the shape every Microsoft sign-in uses, so
          // nobody has to read the label to know which door this is.
          <button
            type="button"
            className="w-full inline-flex items-center justify-center gap-2.5 rounded-full border border-line bg-white px-4 py-2.5 text-sm font-medium text-ink hover:bg-bg-soft disabled:opacity-60 transition-colors"
            disabled={busy}
            onClick={() => void signIn()}
            data-testid="staff-auth-signin"
          >
            <MicrosoftMark />
            {state.status === 'signing-in' ? 'Logger inn…' : 'Logg inn med Microsoft'}
          </button>
        )}
      </div>

      {probe !== null && (
        <p className="mt-2 text-[11px] text-ink-3" data-testid="staff-auth-probe">
          {probe.text}
        </p>
      )}

      {!available && <p className="mt-2 text-[11px] text-ink-3">Microsoft-innlogging er av.</p>}
    </div>
  );
}
